/**
 * token-price.js — "N tokens ≈ X in your money", once, for every paid surface.
 *
 * Before this, eight tool pages formatted `cost_tokens` by hand and every one of
 * them showed TOKENS ONLY, so "2000 tokens" told the buyer nothing about whether
 * the action costs pennies or a pack. The money side could not be baked into the
 * HTML either: tool pages are CF-cached, and the token store already shipped a
 * EUR label to a USD guest that way. So the rate arrives from /api/geo-currency
 * (no-store, per-request) and the label is rendered here.
 *
 * Two ways to use it, pick the smaller one:
 *
 *   declarative — nothing to call, works for a fixed price:
 *       <span data-token-cost="2000"></span>
 *       <span data-token-cost="2000" data-token-cost-format="full"></span>
 *
 *   imperative — when the number depends on the input (duration, chars…):
 *       await window.__tokenPrice.ready();
 *       el.textContent = window.__tokenPrice.text(tokens);
 *
 * The token COUNT belongs to the tool (pricing shapes genuinely differ — per
 * char, per minute, per second-block); this module only turns it into money.
 */
(function () {
    'use strict';

    var state = null;      // {rate, currency, fmt} once resolved
    var inflight = null;

    /* Thousands separator MUST be a NO-BREAK SPACE (U+00A0). A plain space is
       bidi-class WS, so in an RTL paragraph "4 000" splits into two number runs
       and renders reversed — Arabic showed «توكن 000 4» before token-spend.blade
       fixed the same thing. NBSP is bidi-class CS and keeps the number one run. */
    function groupDigits(n) {
        return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    function fetchRate() {
        if (state) return Promise.resolve(state);
        if (inflight) return inflight;
        inflight = fetch('/api/geo-currency', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (j) {
                state = (j && typeof j.token_rate === 'number' && j.token_rate > 0)
                    ? { rate: j.token_rate, currency: j.token_rate_currency || '', fmt: j.token_format || null }
                    : { rate: 0, currency: '', fmt: null };   // resolved-but-empty: never retry-storm
                return state;
            })
            .catch(function () { state = { rate: 0, currency: '', fmt: null }; return state; })
            .finally(function () { inflight = null; });
        return inflight;
    }

    /** Money part only, e.g. "≈ 16 ₽" / "≈ $0.20". Empty string when unknown. */
    function money(tokens) {
        if (!state || !state.rate || !(tokens > 0)) return '';
        var amount = tokens * state.rate;
        // Formatting parts come from the server (config/currencies.format), never
        // from parsing a rendered label — reading a display string back apart is
        // how a second, drifting source of truth is born.
        var f = state.fmt || { symbol: '', pos: 'before', dec: '.', decimals: 2 };
        var dec = typeof f.decimals === 'number' ? f.decimals : 2;
        // A per-action price that rounds to zero says nothing, so keep adding
        // decimals until it does not (₽ is declared 0-decimals for whole prices).
        while (dec < 4 && Number(amount.toFixed(dec)) === 0) dec++;
        var n = dec === 0 ? groupDigits(amount) : amount.toFixed(dec);
        if (dec > 0 && f.dec && f.dec !== '.') n = n.replace('.', f.dec);
        var sym = f.symbol || '';
        return '≈ ' + (f.pos === 'after' ? n + ' ' + sym : sym + n);
    }

    /** Tokens + money, e.g. "2 000 · ≈ 16 ₽". Falls back to tokens alone. */
    function text(tokens) {
        var t = groupDigits(tokens);
        var m = money(tokens);
        return m ? t + ' · ' + m : t;
    }

    function paint(root) {
        var nodes = (root || document).querySelectorAll('[data-token-cost]');
        for (var i = 0; i < nodes.length; i++) {
            var el = nodes[i];
            var n = parseInt(el.getAttribute('data-token-cost'), 10);
            if (!(n > 0)) continue;
            el.textContent = el.getAttribute('data-token-cost-format') === 'full' ? text(n) : (money(n) || '');
            if (!el.textContent) el.hidden = true;      // unknown rate → say nothing, don't show "≈ "
        }
    }

    window.__tokenPrice = {
        ready: fetchRate,
        money: money,
        text: text,
        paint: paint,
        get currency() { return state ? state.currency : ''; },
        get rate() { return state ? state.rate : 0; },
    };

    // Auto-paint whatever is already on the page. A tool that adds nodes later
    // calls paint() again — cheap, idempotent, no observer to leak.
    function boot() { fetchRate().then(function () { paint(document); }); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
})();
